import React from 'react';
import clsx from 'clsx';
import { CheckInCircle } from '../../../icons/check-in-circle';
import { Circle } from '../../../icons/circle';
import { MinusInCircle } from '../../../icons/minus-in-circle';
import { LineItemIcon, MediaPlanIcon } from '../../layout/nav-icons';
import { CellContainer } from './utils';
import { PlanStatusFilterValues } from 'shared/src/domain/line-item/line-item-types';
import { SetFilterValuesType } from '../table-utils';
import { CampaignTableRow } from 'shared/src/api/campaigns';
import { isNil } from 'lodash';
import {
  calcLineItemsWithMediaPlansPct,
  calcLineItemsWithPlatformsPct,
  calcMediaPlanWithPlatformBuyPct,
  calcPriceAllocationPct
} from '../../feedback/plan-status-tooltip';
import { isCampaignTotalsRow } from '../../../features/zoho-campaigns/all-campaigns';
import {
  AllLineItemTableData,
  isLineItemDetailRow,
  isLineItemTotalsRow
} from '../../../features/line-items/all-line-items';

type PlanStatus = 'complete' | 'incomplete' | 'pending';
type Props = {
  status: PlanStatus;
  entityIcon: React.ReactNode;
};

export function PlanStatusIndicator({ status, entityIcon }: Props) {
  const bgColor = calcBgColor(status);
  const icon = calcIcon(status);

  return (
    <CellContainer>
      <div className="w-full rounded-lg bg-gray-100 p-1">
        <div
          className={clsx(
            'bg-linear-to-r flex grow cursor-pointer items-center justify-start gap-1 rounded-sm p-1.5',
            bgColor
          )}>
          {icon}
          {entityIcon}
        </div>
      </div>
    </CellContainer>
  );
}

function calcBgColor(status: PlanStatus) {
  switch (status) {
    case 'complete':
      return 'from-[#BEF264] to-[#A3E635]';
    case 'incomplete':
      return 'from-[#FDE047] to-[#FACC15]';
    case 'pending':
      return 'from-[#F3F4F6] to-[#EFEFEF]';
  }
}

function calcIcon(status: PlanStatus) {
  switch (status) {
    case 'complete':
      return <CheckInCircle />;
    case 'incomplete':
      return <Circle />;
    case 'pending':
      return <MinusInCircle />;
  }
}

export function LineItemPlanStatusIndicator({ row }: { row: AllLineItemTableData | undefined }) {
  if (isNil(row) || isLineItemTotalsRow(row) || !isLineItemDetailRow(row)) return null;
  const entityIcon = <MediaPlanIcon />;
  const status = row.num_media_plans > 0 ? 'complete' : 'pending';
  return <PlanStatusIndicator status={status} entityIcon={entityIcon} />;
}

type CampaignIndicatorProps = {
  row: CampaignTableRow | undefined;
};

export function CampaignLineItemPlanStatusIndicator({ row }: CampaignIndicatorProps) {
  if (!row || isCampaignTotalsRow(row)) return null;

  const priceAllocationPct = calcPriceAllocationPct(row);
  return priceAllocationPct === undefined ? (
    <PlanStatusIndicator status="pending" entityIcon={<LineItemIcon />} />
  ) : priceAllocationPct === 100 ? (
    <PlanStatusIndicator status="complete" entityIcon={<LineItemIcon />} />
  ) : (
    <PlanStatusIndicator status="incomplete" entityIcon={<LineItemIcon />} />
  );
}

export function CampaignMediaPlanPlanStatusIndicator({ row }: CampaignIndicatorProps) {
  if (!row || isCampaignTotalsRow(row)) return null;

  const lineItemsWithPlatformsPct = calcLineItemsWithPlatformsPct(row);
  const lineItemsWithMediaPlansPct = calcLineItemsWithMediaPlansPct(row);
  const mediaPlanWithPlatformBuyPct = calcMediaPlanWithPlatformBuyPct(row);

  if (
    isNil(lineItemsWithPlatformsPct) ||
    isNil(lineItemsWithMediaPlansPct) ||
    isNil(mediaPlanWithPlatformBuyPct)
  ) {
    return <PlanStatusIndicator status="pending" entityIcon={<MediaPlanIcon />} />;
  } else if (
    lineItemsWithPlatformsPct === 100 &&
    lineItemsWithMediaPlansPct === 100 &&
    mediaPlanWithPlatformBuyPct === 100
  ) {
    return <PlanStatusIndicator status="complete" entityIcon={<MediaPlanIcon />} />;
  } else return <PlanStatusIndicator status="incomplete" entityIcon={<MediaPlanIcon />} />;
}

export const planStatusFilterValues: SetFilterValuesType<PlanStatusFilterValues> = [
  { id: 'complete', name: 'Complete' },
  { id: 'pending', name: 'Pending' }
];
